/**
 * WorksPage — `/trabalhos`: os serviços da empresa agrupados por FASE.
 *
 * O app tinha lista de orçamentos, de OS, de contratos e de recibos — e nenhuma
 * de TRABALHOS. Esta tela é a pergunta que o prestador realmente faz ("o que
 * está em aberto?"), que nenhuma das outras responde, porque cada uma vê só a
 * própria fatia do mesmo serviço.
 *
 * Não há porcentagem nem etapa obrigatória: cada trabalho aparece com a fase
 * pelo NOME e uma trilha compacta de cinco pontos. Ver o cabeçalho de
 * `src/lib/work.ts` para o porquê.
 *
 * Grátis para todo mundo — é a tela que torna contrato e nota fiscal visíveis
 * para quem não assina.
 *
 * ─── O DESENHO OFICIAL NUMA LISTA DERIVADA ──────────────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`, `/clients` e `/catalog`. O que
 * precisou ser TRADUZIDO aqui (o exercício do #182/#188/#195):
 *
 * · O EYEBROW diz o "onde" — a família de que a lista faz parte.
 * · O VALOR AO VIVO é a soma do recorte visível, e a regra de quem entra nela é
 *   de NEGÓCIO: trabalho `lost` é dinheiro que não vai entrar e fica FORA, com
 *   a contagem do que ficou de fora escrita na sublinha ("no silent caps").
 *   Mora no módulo puro `worksListFilters`.
 * · O BOTÃO DE VOLTAR fica, diferente de `/quotes` e `/clients`: `/trabalhos`
 *   NÃO é destino da navegação inferior, então sem ele não há saída barata.
 * · As abas Em aberto/Todos continuam `<Tabs>` AZUIS herdadas do componente
 *   base (#25): elas são a única troca de CONTEXTO da tela. A fase é recorte, e
 *   recorte é faixa de botões planos (#191).
 * · O agrupamento por fase NÃO saiu. Ele responde "onde cada coisa está" para
 *   quem só chegou; a faixa de botões responde "me mostre só isto". Com uma
 *   fase escolhida sobra um grupo só, e o cabeçalho dele confirma o recorte.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpDown,
  Check,
  ChevronRight,
  Search,
  SlidersHorizontal,
  Workflow,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import { useWorks } from '@/hooks/useWorks';
import {
  groupWorksByPhase,
  WORK_STAGE_ORDER,
  WORK_STAGE_SHORT_LABELS,
  type WorkListEntry,
  type WorkStage,
} from '@/lib/work';
import {
  DEFAULT_WORK_SORT,
  WORK_PERIOD_PRESETS,
  WORK_SORT_LABELS,
  WORK_SORT_ORDER,
  countActiveWorkFilters,
  countWorksByPhase,
  isDeadWorkPhaseFilter,
  matchesWorkPeriod,
  matchesWorkPhase,
  matchesWorkScope,
  matchesWorkValue,
  phaseFilterFitsScope,
  sortWorksBy,
  summarizeWorkList,
  workListTotalLabel,
  workMatchesSearch,
  workPhaseFilters,
  type WorkPhaseFilter,
  type WorkScope,
  type WorkSortOption,
} from '@/lib/worksListFilters';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';
import { PERIOD_PRESET_LABELS, resolvePeriodPreset, type ReportPeriodPreset } from '@/lib/reportPeriod';
import { CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { plural } from '@/lib/plural';
import { cn } from '@/lib/utils';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const PERIOD_GROUP_KEY = 'period';

/** Rota do documento raiz — as rotas do app são bilíngues (#56). */
function routeForEntry(entry: WorkListEntry): string {
  if (entry.root.kind === 'quote') return `/quotes/${entry.root.id}`;
  if (entry.root.kind === 'service_order') return `/ordens-servico/${entry.root.id}`;
  return `/contratos/${entry.root.id}`;
}

/**
 * Selo do documento que ABRIU o trabalho — só quando não é o caso comum.
 *
 * O orçamento fica `null` de propósito: ele é a origem da esmagadora maioria
 * dos trabalhos, e um selo em toda linha roubaria largura do título sem
 * distinguir nada (#175). `receipt` também é `null`, mas por outro motivo:
 * recibo NUNCA é raiz (ver `buildWorkList`), então esta entrada existe só para
 * o tipo fechar.
 */
const ROOT_KIND_LABELS: Record<WorkListEntry['root']['kind'], string | null> = {
  quote: null,
  service_order: 'OS avulsa',
  contract: 'Contrato avulso',
  receipt: null,
};

/** Descreve a trilha em texto, para leitor de tela ("Orçamento: concluído; …"). */
function stagesAriaLabel(stages: WorkStage[]): string {
  const estado: Record<WorkStage['state'], string> = {
    done: 'concluído',
    current: 'em andamento',
    unused: 'não usado',
  };
  return stages
    .map((s) => `${WORK_STAGE_SHORT_LABELS[s.id]}: ${estado[s.state]}`)
    .join('; ');
}

function stageDotClass(stage: WorkStage): string {
  if (stage.state === 'done') return 'bg-primary';
  if (stage.state === 'current') {
    const cancelled = stage.detail?.startsWith('Cancel') || stage.detail === 'Recusado';
    return cancelled ? 'bg-muted-foreground/30' : 'bg-amber-500';
  }
  return 'bg-muted-foreground/20';
}

export default function WorksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { entries, truncated, isLoading } = useWorks();

  const [scope, setScope] = useState<WorkScope>('open');
  const [search, setSearch] = useState('');
  const [phaseFilter, setPhaseFilter] = useState<WorkPhaseFilter>('all');
  const [sortOption, setSortOption] = useState<WorkSortOption>(DEFAULT_WORK_SORT);
  const [periodPreset, setPeriodPreset] = useState<ReportPeriodPreset | null>(null);
  const [valueRange, setValueRange] = useState<ListValueRange>(EMPTY_LIST_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Trocar de aba pode deixar o recorte de fase órfão: "Pago" não existe em
   * "Em aberto", e a tela ficaria vazia sem nenhum controle explicando por quê.
   */
  useEffect(() => {
    setPhaseFilter((current) => (phaseFilterFitsScope(current, scope) ? current : 'all'));
  }, [scope]);

  /**
   * A faixa do período é resolvida UMA vez por render, e não dentro do
   * `filter`: `resolvePeriodPreset` cria `Date`s a partir de "agora", e chamá-la
   * por trabalho compararia cada linha contra um "agora" diferente.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolvePeriodPreset(periodPreset) : null),
    [periodPreset],
  );

  /** O recorte SEM a fase — é ele que alimenta as contagens dos botões. */
  const worksBeforePhase = useMemo(
    () =>
      entries.filter(
        (entry) =>
          matchesWorkScope(entry, scope) &&
          workMatchesSearch(entry, search) &&
          matchesWorkPeriod(entry, periodRange) &&
          matchesWorkValue(entry, valueRange),
      ),
    [entries, scope, search, periodRange, valueRange],
  );

  const phaseCounts = useMemo(() => countWorksByPhase(worksBeforePhase), [worksBeforePhase]);
  const phaseOptions = useMemo(() => workPhaseFilters(scope), [scope]);

  const visible = useMemo(
    () =>
      sortWorksBy(
        worksBeforePhase.filter((entry) => matchesWorkPhase(entry, phaseFilter)),
        sortOption,
      ),
    [worksBeforePhase, phaseFilter, sortOption],
  );

  const groups = useMemo(() => groupWorksByPhase(visible), [visible]);
  const openCount = useMemo(
    () => entries.filter((entry) => matchesWorkScope(entry, 'open')).length,
    [entries],
  );

  /**
   * `includeDead` liga quando o recorte É a fase morta: com o filtro em
   * "Perdido", excluir todo mundo faria o hero anunciar R$ 0,00 com a lista
   * cheia embaixo.
   */
  const summary = summarizeWorkList(visible, { includeDead: isDeadWorkPhaseFilter(phaseFilter) });
  const activeModalFilters = countActiveWorkFilters(periodPreset, valueRange);
  const recorteAtivo =
    search.trim().length > 0 || phaseFilter !== 'all' || periodPreset !== null;

  const filterGroups: FilterGroup[] = [
    {
      key: PERIOD_GROUP_KEY,
      label: 'Começou em',
      options: [
        // "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de seleção:
        // sem ela, quem escolheu um período não teria como voltar atrás.
        { value: '', label: 'Qualquer data' },
        ...WORK_PERIOD_PRESETS.map((preset) => ({
          value: preset,
          label: PERIOD_PRESET_LABELS[preset],
        })),
      ],
    },
  ];

  if (!user) {
    return (
      <MainLayout showFab={false}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Workflow className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Trabalhos</h2>
          <p className="mb-4 text-muted-foreground">Faça login para continuar.</p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app                                     */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            {/* `/trabalhos` não está na navegação inferior: sem este botão, a
                única saída da tela é o gesto do sistema. */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate('/dashboard')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Fluxo</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">Trabalhos</h1>
            </div>
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">{workListTotalLabel(scope, phaseFilter)}</p>
            {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                (#159-b): escrever uma regra local aqui seria mais uma cópia de
                uma medida que já divergiu entre telas uma vez. */}
            <p
              className={cn(
                'truncate font-bold leading-tight',
                heroTotalSizeClass(formatCurrency(summary.total)),
              )}
            >
              {formatCurrency(summary.total)}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {summary.count === 0
                ? 'Nenhum trabalho neste filtro'
                : plural(summary.count, 'trabalho', 'trabalhos')}
              {/* "no silent caps": o que ficou fora da soma é DITO. Sem isto o
                  total apareceria menor do que a lista sugere, sem explicação. */}
              {summary.excludedCount > 0 &&
                ` · ${plural(
                  summary.excludedCount,
                  'perdido fora da conta',
                  'perdidos fora da conta',
                )}`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* Aba: a única troca de CONTEXTO da tela (#25). */}
          <Tabs value={scope} onValueChange={(v) => setScope(v as WorkScope)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="open">
                Em aberto{openCount > 0 ? ` (${openCount})` : ''}
              </TabsTrigger>
              <TabsTrigger value="all">Todos</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Corte silencioso é proibido: se a leitura bateu no teto de páginas,
              a lista está incompleta e "não tenho mais nada em aberto" seria a
              conclusão errada (armadilha #50). */}
          {truncated && (
            <div className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Você tem muitos documentos e nem todos couberam nesta lista. Alguns trabalhos podem
                não aparecer aqui.
              </span>
            </div>
          )}

          {/* BLOCO DE CONTROLES DA LISTA — busca, recorte e ordenação ativa.
              Eles são um grupo só (todos respondem "o que eu estou vendo"),
              então ficam juntos com `space-y-2` em vez do `space-y-5` que separa
              as SEÇÕES da tela (#191/#194). */}
          <div className="space-y-2">
            {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o
                campo é `min-w-0 flex-1`: sem isso o input empurraria os botões
                para fora da tela em vez de encolher (#186). */}
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Trabalho ou cliente"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Ordenação: menu, não modal — é uma escolha única entre seis
                  rótulos curtos, e um diálogo seria dois cliques a mais pelo
                  mesmo resultado. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    aria-label={`Ordenar (${WORK_SORT_LABELS[sortOption]})`}
                    title={`Ordenar: ${WORK_SORT_LABELS[sortOption]}`}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {WORK_SORT_ORDER.map((option) => (
                    <DropdownMenuItem
                      key={option}
                      onSelect={() => setSortOption(option)}
                      className="gap-2"
                    >
                      {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                          placeholder, o rótulo escolhido fica deslocado. */}
                      <span className="flex h-4 w-4 items-center justify-center">
                        {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                      </span>
                      {WORK_SORT_LABELS[option]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="icon"
                className="relative h-10 w-10 shrink-0 rounded-xl"
                onClick={() => setFiltersOpen(true)}
                aria-label={
                  activeModalFilters > 0
                    ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                    : 'Filtros'
                }
                title="Filtros"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeModalFilters > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                    {activeModalFilters}
                  </span>
                )}
              </Button>
            </div>

            {/* FASE — botões planos, não alternador. A contagem vai no
                `title`/`aria-label`, onde não custa pixel (#191). */}
            <div className={FILTER_CHIP_ROW}>
              {phaseOptions.map((option) => {
                const active = phaseFilter === option.value;
                const count = phaseCounts[option.value] ?? 0;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPhaseFilter(option.value)}
                    aria-pressed={active}
                    aria-label={`${option.label} (${count})`}
                    title={`${option.label}: ${plural(count, 'trabalho', 'trabalhos')}`}
                    className={cn(FILTER_CHIP_COMPACT, active ? FILTER_CHIP_ON : FILTER_CHIP_OFF)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* A ordenação ativa é a única decisão da tela que não se anuncia
                sozinha: fase tem botões e período/valor têm o selo do ícone, mas
                "por que este trabalho está no topo?" só se responde abrindo o
                menu. É rótulo, não botão — o controle é o ícone logo acima, e um
                segundo gatilho dividiria o alvo do toque.

                Do lado direito vive a LEGENDA da trilha: as cinco barrinhas de
                cada card não diziam nada por si, e quem nunca viu o módulo não
                tinha como descobrir que são as ETAPAS do trabalho. */}
            {!isLoading && visible.length > 0 && (
              <div className="space-y-1 px-1">
                <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
                  <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{WORK_SORT_LABELS[sortOption]}</span>
                </span>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  As barrinhas são as etapas de cada trabalho:{' '}
                  {WORK_STAGE_ORDER.map((id) => WORK_STAGE_SHORT_LABELS[id]).join(' › ')}.
                </p>
              </div>
            )}

            {/* Lista — DENTRO do mesmo grupo `space-y-2` dos controles, e não um
                `-mt-N` competindo com o `space-y-5` de fora: margem negativa num
                filho direto PERDE a briga de especificidade contra o
                `space-y-*` do container (#194). */}
            {isLoading && entries.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={cn(CONTENT_CARD, 'h-24 animate-pulse')} />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-8 text-center',
                )}
              >
                <Workflow className="mb-3 h-12 w-12 text-muted-foreground/50" />
                <p className="font-medium">
                  {recorteAtivo
                    ? 'Nenhum trabalho encontrado'
                    : scope === 'open'
                      ? 'Nenhum trabalho em aberto'
                      : 'Nenhum trabalho ainda'}
                </p>
                <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                  {recorteAtivo
                    ? 'Nenhum trabalho corresponde à busca e aos filtros escolhidos.'
                    : scope === 'open'
                      ? 'Tudo que você começou já foi pago ou encerrado. Um orçamento novo abre o próximo trabalho.'
                      : 'Cada orçamento, contrato ou ordem de serviço abre um trabalho, e os documentos seguintes entram nele sozinhos.'}
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {groups.map((group) => (
                  <div key={group.phase}>
                    <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label} · {group.entries.length}
                    </p>
                    {/* Duas colunas no desktop DENTRO de cada grupo de fase: o
                        agrupamento continua sendo a estrutura da tela, e o que
                        muda é quantos trabalhos cabem por linha. `grid-cols-1`
                        explícito no celular (#185). */}
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                      {group.entries.map((entry) => {
                        const rootLabel = ROOT_KIND_LABELS[entry.root.kind];
                        return (
                          <div
                            key={`${entry.root.kind}-${entry.root.id}`}
                            className={cn(
                              CONTENT_CARD,
                              'cursor-pointer p-4 transition-shadow hover:shadow-md',
                            )}
                            onClick={() => navigate(routeForEntry(entry))}
                          >
                            {/* O CARD é o flex row e a coluna da esquerda dita a
                                altura: é o `items-center` daqui que centra o
                                valor contra o card inteiro, e não contra a linha
                                do título, que muda de altura conforme quebra
                                (#189). */}
                            <div className="flex items-center gap-3">
                              <div className="min-w-0 flex-1 space-y-2">
                                {/* LINHA DE IDENTIFICAÇÃO: tudo que identifica o
                                    trabalho mas não é o conteúdo dele, o que
                                    devolve a largura inteira ao título. */}
                                {(entry.sortDate || rootLabel) && (
                                  <div className="flex flex-wrap items-center gap-2">
                                    {entry.sortDate && (
                                      /* Data pelo calendário LOCAL (date-fns
                                         sobre `Date`): cortar a string no 'T'
                                         daria a data em UTC e imprimiria o DIA
                                         SEGUINTE para tudo criado depois das
                                         21h (#114). */
                                      <Badge
                                        variant="outline"
                                        className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                                      >
                                        {format(entry.sortDate, 'dd/MM/yyyy', { locale: ptBR })}
                                      </Badge>
                                    )}
                                    {rootLabel && (
                                      <Badge
                                        variant="outline"
                                        className="h-5 border-primary/30 bg-primary/5 px-1.5 py-0 text-xs text-primary"
                                      >
                                        {rootLabel}
                                      </Badge>
                                    )}
                                  </div>
                                )}

                                {/* `min-w-0` não é decoração (#175): sem ele um
                                    flex item nasce com `min-width: auto` e um
                                    título sem espaços estica a linha para fora
                                    da viewport. E sem `truncate`: é o título que
                                    distingue duas linhas da mesma lista. */}
                                <div className="min-w-0 space-y-0.5">
                                  <p className="min-w-0 break-words font-bold">
                                    {entry.work.title}
                                  </p>
                                  {entry.work.clientName && (
                                    <p className="break-words text-sm text-muted-foreground">
                                      {entry.work.clientName}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="flex shrink-0 items-center gap-1">
                                {entry.work.total > 0 && (
                                  <span className="font-semibold">
                                    {formatCurrency(entry.work.total)}
                                  </span>
                                )}
                                <ChevronRight className="h-4 w-4 opacity-50" aria-hidden="true" />
                              </div>
                            </div>

                            {/* Trilha compacta: cinco pontos, sem rótulo — o
                                nome da fase já está no cabeçalho do grupo e a
                                legenda acima da lista explica a ordem. */}
                            <div className="mt-3 flex items-center gap-2">
                              {/* `role="img"` + rótulo: antes era `aria-hidden`,
                                  ou seja, a única informação de progresso do
                                  card não existia para leitor de tela. */}
                              <div
                                className="flex gap-1"
                                role="img"
                                aria-label={stagesAriaLabel(entry.work.stages)}
                              >
                                {entry.work.stages.map((stage) => (
                                  <span
                                    key={stage.id}
                                    className={cn('h-1.5 w-6 rounded-full', stageDotClass(stage))}
                                  />
                                ))}
                              </div>
                              {entry.work.nextStep && (
                                <span className="truncate text-[11px] text-muted-foreground">
                                  {entry.work.nextStep.label}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtros: quando o trabalho começou e a faixa de valor.
          A FASE aparece só na faixa de botões, e não aqui, porque ela já se
          anuncia sozinha na tela — repeti-la no modal duplicaria o controle sem
          revelar nada que o usuário não esteja vendo. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte os trabalhos por quando começaram e por valor."
        groups={filterGroups}
        rangeLabel="Valor do trabalho"
        value={{ groups: { [PERIOD_GROUP_KEY]: periodPreset ?? '' }, range: valueRange }}
        onApply={(next) => {
          const wanted = next.groups[PERIOD_GROUP_KEY];
          setPeriodPreset(
            wanted && WORK_PERIOD_PRESETS.includes(wanted as ReportPeriodPreset)
              ? (wanted as ReportPeriodPreset)
              : null,
          );
          setValueRange(next.range);
        }}
      />
    </MainLayout>
  );
}
